import { useState, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextareaAutosize } from '@electricjs/arc';
import styled from 'styled-components';
import { spaces } from '@electricjs/arc';

type CustomChatInputProps = {
  inProgress: boolean;
  onSend: (text: string) => Promise<void>;
  inputId: string;
  disabled?: boolean;
};

const Form = styled.form`
  display: flex;
  width: 100%;
  max-width: 120rem;
  margin: ${spaces.space_xs_rem} auto;
  gap: ${spaces.space_s_rem};
  align-items: center;
`;

const InputContainer = styled.div`
  flex: 1;
  min-width: 0;

  & > div {
    width: 100%;
  }
`;

/**
 * Custom Input component for CopilotChat that matches Arc design system
 */
export const CustomChatInput = ({
  inProgress,
  onSend,
  inputId,
  disabled = false,
}: CustomChatInputProps) => {
  const { t } = useTranslation('ai-chat');
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSendMessage = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (message.trim() && !inProgress && !disabled) {
      onSend(message);
      setMessage('');
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Form onSubmit={handleSendMessage}>
      <InputContainer>
        <TextareaAutosize
          id={inputId}
          placeholder={
            disabled ? t('inputPlaceholderDisabled') : t('inputPlaceholder')
          }
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          minRows={1}
          maxRows={5}
          submitOnEnter={true}
          disabled={disabled}
        />
      </InputContainer>
      <Button
        id="copilot-chat-send-button"
        iconBefore="paper-plane"
        variant="fill"
        disabled={!message || disabled}
        type="submit"
        loading={inProgress}
      />
    </Form>
  );
};
